import styled from '@emotion/styled';
import type { ExtractedAnswersAPI } from '@trinity/chat';
import { Accordion } from '../Accordion';
import { ExtractedAnswerCard } from './ExtractedAnswerCard';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TagLabelWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TagColorDot = styled.span<{ color: string }>`
  width: 16px;
  height: 16px;
  min-width: 10px;
  border-radius: 50%;
  background-color: ${(p) => p.color};
  display: inline-block;
`;

const TagName = styled.span`
  font-family: var(--font-semibold);
  color: var(--black-l);
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: var(--gray-d);
  font-size: 14px;
`;

type TagGroup = {
  tagId: number;
  tagName: string;
  tagColor: string;
  tagOrdinal: number;
  items: ExtractedAnswersAPI.GetExtractedAnswers.ExtractedAnswerItem[];
};

type TagGroupAccordionProps = {
  group: TagGroup;
  transcriptId: number;
};

function TagGroupAccordion({ group, transcriptId }: TagGroupAccordionProps): React.ReactElement {
  const sortedItems = [...group.items].sort((a, b) => a.questionOrdinal - b.questionOrdinal);

  const renderLabel = () => (
    <TagLabelWrap>
      <TagColorDot color={group.tagColor} />
      <TagName>{group.tagName}</TagName>
    </TagLabelWrap>
  );

  return (
    <Accordion label={renderLabel} showCount={false} grows={false}>
      <CardList>
        {sortedItems.map((item) => (
          <ExtractedAnswerCard key={item.id} item={item} transcriptId={transcriptId} />
        ))}
      </CardList>
    </Accordion>
  );
}

type Props = {
  items: ExtractedAnswersAPI.GetExtractedAnswers.ExtractedAnswerItem[];
  transcriptId: number;
};

export function ExtractedAnswersList({ items, transcriptId }: Props): React.ReactElement {
  if (items.length === 0) {
    return <EmptyState>No extracted answers yet.</EmptyState>;
  }

  // Group by conference tag
  const tagGroupMap = new Map<number, TagGroup>();
  const untagged: ExtractedAnswersAPI.GetExtractedAnswers.ExtractedAnswerItem[] = [];

  for (const item of items) {
    const tag = item.tag;
    if (tag) {
      const existing = tagGroupMap.get(tag.id);
      if (existing) {
        existing.items.push(item);
      } else {
        tagGroupMap.set(tag.id, {
          tagId: tag.id,
          tagName: tag.name,
          tagColor: tag.color,
          tagOrdinal: tag.ordinal,
          items: [item],
        });
      }
    } else {
      untagged.push(item);
    }
  }

  // Sort tag groups by their ordinal
  const sortedGroups = [...tagGroupMap.values()].sort((a, b) => a.tagOrdinal - b.tagOrdinal);

  return (
    <Container>
      {sortedGroups.map((group) => (
        <TagGroupAccordion key={group.tagId} group={group} transcriptId={transcriptId} />
      ))}
      {untagged.map((item) => (
        <ExtractedAnswerCard key={item.id} item={item} transcriptId={transcriptId} />
      ))}
    </Container>
  );
}
